//Proofed
import PublicIcon from '@mui/icons-material/Public'
import PeopleIcon from '@mui/icons-material/People'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import SearchIcon from '@mui/icons-material/Search'
import SchoolIcon from '@mui/icons-material/School'
import type { HowItWorksStep } from '../components/HowItWorksSection'

// Hero right-panel highlights
export const growOverseasHighlights = [
    'Monitor international procurement markets alongside your UK pipeline',
    'Adapt your bid content and win themes for different procurement cultures',
    'Deploy dedicated licences for regional BD leads covering different markets',
    'Use your existing knowledge base to draft bids that work across borders',
    'Get expert strategy support as you navigate new procurement frameworks',
]

// Challenge cards  section 2
export const growOverseasChallenges = [
    {
        heading: "International procurement has different rules, portals and expectations to the UK",
        body: "European and overseas public procurement operates under different legal frameworks, evaluation criteria and cultural expectations. What works in a UK bid doesn't always translate. Entering new markets without understanding them leads to wasted effort and costly losses",
    },
    {
        heading: "You have UK case studies and a strong track record but buyers don't always recognise them",
        body: "UK experience and credentials don't automatically resonate with overseas buyers. Procurement cultures vary significantly in how you present capability, social value, pricing and past performance. They need to be adapted for each market or your track record won't land the way it should",
    },
    {
        heading: "Coordinating BD across multiple markets and time zones stretches your team to breaking point",
        body: "Running a UK BD operation is demanding enough. Adding European or international markets without the right structure means regional leads are either underserved with intelligence, overwhelmed with irrelevant alerts, or both. Coordination breaks down and opportunities are lost",
    },
]

// Recommended solution stack  section 3
export const growOverseasSolutionStack = [
    {
        Icon: SearchIcon,
        title: 'CFP Core Pro: International Monitoring',
        body: 'Monitor UK and international procurement markets with dedicated licences for each regional BD lead. Consistent daily intelligence regardless of geography. One platform, every market',
        tag: 'Platform',
        link: '/products/core',
        linkLabel: 'Learn about Core Pro',
    },
    {
        Icon: AutoAwesomeIcon,
        title: 'Tender Ninja',
        body: 'Adapt your established bid content and knowledge base for international markets. Faster drafting that builds on your existing wins, drawing on your track record and win themes applied intelligently across borders',
        tag: 'Platform Add-on',
        link: '/products/tender-ninja',
        linkLabel: 'Learn about Tender Ninja',
    },
    {
        Icon: SchoolIcon,
        title: 'Bid & BD Advisory',
        body: 'Expert support for your first international submissions. Strategy, positioning, bid writing and local market guidance from people who\'ve navigated international procurement before',
        tag: 'Advisory',
        link: '/products/bid-services',
        linkLabel: 'Learn about Bid Services',
    },
]

// Journey steps  section 4
export const growOverseasSteps: HowItWorksStep[] = [
    {
        num: '1',
        Icon: PublicIcon,
        title: 'Map Your Target Markets',
        body: 'Work with our advisory team to identify the international markets best suited to your capability and growth ambitions. Understand the procurement landscape before committing resource',
    },
    {
        num: '2',
        Icon: PeopleIcon,
        title: 'Assign Regional Licences',
        body: 'Give each regional BD lead their own Core Pro licence configured for their specific market. Focused intelligence per market',
    },
    {
        num: '3',
        Icon: AutoAwesomeIcon,
        title: 'Adapt and Draft',
        body: 'Use Tender Ninja to adapt your UK knowledge base and bid content for international buyer expectations. Your track record, repositioned to resonate in new markets',
    },
    {
        num: '4',
        Icon: EmojiEventsIcon,
        title: 'Submit and Learn',
        body: 'With expert advisory support for your first international submissions, reduce the risk of early losses. Build an international track record from a strong foundation',
    },
]

// Outcomes checklist  section 5
export const growOverseasOutcomes = [
    'A clear view of international procurement opportunities alongside your UK pipeline',
    'Bid content that adapts your UK track record for overseas buyer expectations',
    'Regional BD leads with focused, relevant intelligence for their specific markets',
    'Expert support for your first international submissions to reduce risk',
    'A structured approach to international expansion, not trial and error',
]

// Self-qualification checklist  section 6
export const growOverseasQualifiers = [
    'You have an established UK public sector track record and want to grow internationally',
    'You have or are building regional BD capability that needs international procurement intelligence',
    'You\'re exploring specific European or international markets and want to understand them before committing',
    'You want expert guidance on your first international submissions, not to learn by losing',
    'You\'re serious about building a long-term international public sector pipeline',
]

// FAQs  section 8
export const growOverseasFaqs = [
    {
        q: 'Does CFP Core Pro cover international procurement?',
        a: "Yes. CFP Core Pro monitors UK procurement portals as standard and can be configured to cover European and international public procurement sources. ",
    },
    {
        q: 'How do we adapt our UK bid content for overseas markets?',
        a: "Tender Ninja works from your uploaded document library: your UK case studies, past bids, CVs and company information. Our advisory team can work with you to identify how your content needs to be repositioned for different procurement cultures: how evidence is presented, how social value is framed, how pricing is structured and which aspects of your track record are most persuasive to overseas buyers.",
    },
    {
        q: 'What advisory support is available for international expansion?',
        a: "Our Bid & BD Advisory service covers the full range of international expansion support: market mapping, entry strategy, content adaptation, bid writing for first international submissions and post-submission review. ",
    },
    {
        q: 'How do dedicated licences work for international teams?',
        a: "Each regional BD lead receives their own Core Pro licence with independently configured alert filters. A UK lead and a European lead can each run different geographic, sector and buyer filters, receiving completely separate, focused daily digests. Licences are managed centrally and can be added or reconfigured as your regional structure evolves.",
    },
    {
        q: 'Do we need local presence to bid internationally?',
        a: "Requirements vary by market and contract. Many European and international public contracts can be bid by non-resident businesses, particularly at framework level or for specialist services. Some markets or contract types do require local presence or a local delivery partner. Our advisory team will advise on the specific requirements for your target markets and help you structure your approach accordingly.",
    },
    {
        q: 'What\'s the best starting point for international expansion?',
        a: "For most businesses, the right starting point is a targeted market mapping exercise. We identify which international markets are most accessible given your existing capability and track record. We can run this as a lightweight advisory engagement before you commit to full monitoring licences or advisory support. From there, you can prioritise markets and build out your regional BD capability systematically.",
    },
]

// Related solution links  section 9
export const growOverseasRelated = [
    {
        headline: 'Expand into New Sectors',
        body: 'Growing your presence in the UK across multiple sectors? Dedicated licences, AI bid writing and strategic review to scale your domestic public sector operation',
        link: '/solutions/expand-into-new-sectors',
        tag: 'Expanding Reach',
    },
    {
        headline: 'Find a Dedicated Bid Partner',
        body: 'Want experts to manage your BD and bid submissions entirely? Our fully managed service handles strategy, monitoring and bid writing on your behalf',
        link: '/solutions/find-a-dedicated-bid-partner',
        tag: 'Execution Support',
    },
]
